import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Button,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import MuiTable from "../../../../common-utilities/core/mui-table";
import DateRangeFilter from "../../../../common-utilities/core/date-range-filter";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import {
  FINANCE_V2_HOSTNAME,
  INTRANETHOSTNAME,
  REPORT_HOSTNAME,
} from "../../../../common-utilities/utils/end-points";
import { FINANCE_REST_URLS } from "../../../../common-utilities/utils/end-points/finance-v2";
import { toast } from "react-toastify";
import { getPRHeadersConfig, PR_STATUSES, PR_TYPES } from "./helper";
import {
  getStartOfDay,
  getEndOfDay,
} from "../../../../common-utilities/utils/time";
import FinancialYearDropdown from "../../home/financial-year-dropdown";
import {
  canViewPRs,
  getPrDepartmentScope,
  isProcurementUser,
} from "../../../utils/common-utils";
import { downloadReports } from "./utils";

const PRList = ({ status }) => {
  const navigate = useNavigate();
  const [prDepartmentScope] = useState(getPrDepartmentScope);
  const [prList, setPRList] = useState({});
  const [searchKey, setSearchKey] = useState("");
  const [filters, setFilters] = useState(() => ({
    limit: 10,
    page: 1,
    sortBy: "-createdAt",
    ...(prDepartmentScope.locked && prDepartmentScope.defaultDepartment != null
      ? { department: prDepartmentScope.defaultDepartment }
      : {}),
  }));
  const [storeList, setStoreList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  // Params of the last list fetch -- the dump reuses these so it matches the rows on screen,
  // not filter/search edits the user hasn't applied with Search yet.
  const appliedParams = useRef({});

  useEffect(() => {
    if (!canViewPRs()) return;
    loadData(filters);
    loadStores();
    loadDepartments();
  }, [status]);

  const loadStores = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.GET_LOCATION_LIST}`
    )
      .then((res) => {
        if (!res.message) setStoreList(res || []);
      })
      .catch(console.log);
  };

  const loadDepartments = () => {
    invokeApi(
      HTTP_METHODS.GET,
      `${INTRANETHOSTNAME}${FINANCE_REST_URLS.DEPARTMENTS}`
    )
      .then((res) => {
        if (!res.message) setDepartmentList(res || []);
      })
      .catch(console.log);
  };

  const loadData = (apiFilters) => {
    let params = { ...apiFilters };
    if (status !== "all") {
      params.status = status;
    }
    if (searchKey) {
      params.searchKey = searchKey;
    }
    if (params.startDate) {
      params.startDate = getStartOfDay(params.startDate);
    }
    if (params.endDate) {
      params.endDate = getEndOfDay(params.endDate);
    }
    if (prDepartmentScope.locked) {
      if (prDepartmentScope.defaultDepartment == null) {
        appliedParams.current = params;
        setPRList({});
        return;
      }
      if (!prDepartmentScope.departmentIds.includes(Number(params.department))) {
        params.department = prDepartmentScope.defaultDepartment;
      }
    }
    appliedParams.current = params;
    fetchPRs(params);
  };

  const fetchPRs = (params, download) => {
    invokeApi(
      HTTP_METHODS.GET,
      `${download ? REPORT_HOSTNAME : FINANCE_V2_HOSTNAME}${FINANCE_REST_URLS.PURCHASE_REQUISITION}`,
      null,
      params
    )
      .then((response) => {
        if (response.message) {
          toast.error(response.message, { autoClose: 2000 });
        } else if (download) {
          downloadReports(response?.results || [], storeList);
        } else {
          setPRList(response);
        }
      })
      .catch((err) => console.log(err));
  };

  const onDownload = () => {
    fetchPRs(
      { ...appliedParams.current, page: 1, limit: prList.totalResults },
      true
    );
  };

  const onSearch = () => {
    const newFilters = { ...filters, page: 1 };
    setFilters(newFilters);
    loadData(newFilters);
  };

  const departmentOptions = prDepartmentScope.restricted
    ? departmentList.filter((dept) =>
        prDepartmentScope.departmentIds.includes(Number(dept._id ?? dept.id))
      )
    : departmentList;

  const selectedDepartment = prDepartmentScope.locked
    ? departmentOptions.find(
        (dept) => Number(dept._id ?? dept.id) === Number(filters.department)
      ) || null
    : undefined;

  if (!canViewPRs()) {
    return (
      <div className="finance-v2-pr-list">No access to view Purchase Requests</div>
    );
  }

  return (
    <div className="finance-v2-pr-list">
      <div className="filter-row">
        {/* Store PRs are only ever visible to Procurement & Supply Chain (see
            getPurchaseRequests) -- a regular department user never sees store PRs at all, so
            this toggle would be meaningless for them and is hidden. Purely a client-side
            display filter on data the backend fetches with prFilter -- pagination stays
            server-side, so this is sent as a query param rather than filtered in the browser. */}
        {isProcurementUser() && (
            <ToggleButtonGroup
              color="primary"
              size="small"
              exclusive
              value={filters.prFilter || "all"}
              onChange={(e, newValue) => {
                if (!newValue) return; // exclusive group -- ignore a click that would deselect
                const newFilters = { ...filters, page: 1 };
                if (newValue === "all") {
                  delete newFilters.prFilter;
                } else {
                  newFilters.prFilter = newValue;
                }
                setFilters(newFilters);
                loadData(newFilters);
              }}
              sx={{
                "& .MuiToggleButton-root": {
                  padding: "6px 14px",
                  fontSize: "0.92rem",
                },
              }}
            >
            <ToggleButton value="all">All PRs</ToggleButton>
            <ToggleButton value="store">Store PRs</ToggleButton>
          </ToggleButtonGroup>
        )}
                <TextField
          placeholder="Search by PR Number"
          value={searchKey}
          size="small"
          onChange={(e) => setSearchKey(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSearch()}
        />
        <Autocomplete
          onChange={(e, val) => {
            if (val) {
              setFilters((prev) => ({ ...prev, prType: val.value }));
            } else {
              setFilters((prev) => {
                const next = { ...prev };
                delete next.prType;
                return next;
              });
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="PR Type"
              sx={{ minWidth: 150 }}
            />
          )}
          options={PR_TYPES}
        />
        <Autocomplete
          onChange={(e, val) => {
            if (val) {
              setFilters((prev) => ({ ...prev, site: val._id || val.id }));
            } else {
              setFilters((prev) => {
                const next = { ...prev };
                delete next.site;
                return next;
              });
            }
          }}
          options={storeList}
          getOptionLabel={(op) =>
            op.code ? `${op.code} - ${op.name}` : op.name || ""
          }
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Store"
              sx={{ minWidth: 200 }}
            />
          )}
        />
        <Autocomplete
          value={selectedDepartment}
          disableClearable={prDepartmentScope.locked}
          onChange={(e, val) => {
            if (val) {
              setFilters((prev) => ({
                ...prev,
                department: val._id || val.id,
              }));
            } else {
              setFilters((prev) => {
                const next = { ...prev };
                delete next.department;
                return next;
              });
            }
          }}
          options={departmentOptions}
          getOptionLabel={(op) => op.name || ""}
          renderInput={(params) => (
            <TextField
              {...params}
              size="small"
              label="Department"
              sx={{ minWidth: 200 }}
            />
          )}
        />
        {status === "all" && (
          <Autocomplete
            onChange={(e, val) => {
              if (val) {
                setFilters((prev) => ({ ...prev, status: val.value }));
              } else {
                setFilters((prev) => {
                  const next = { ...prev };
                  delete next.status;
                  return next;
                });
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                size="small"
                label="Status"
                sx={{ minWidth: 180 }}
              />
            )}
            options={PR_STATUSES}
          />
        )}
        <FinancialYearDropdown filters={filters} setFilters={setFilters} />
        <DateRangeFilter
          startDate={filters?.startDate}
          endDate={filters?.endDate}
          setDateRange={setFilters}
        />
      </div>
      <div className="end-btns">
        <Button variant="outlined" onClick={onSearch}>
          Search
        </Button>
        <Button
          variant="outlined"
          disabled={!prList.totalResults}
          onClick={onDownload}
        >
          Download
        </Button>
      </div>
      <MuiTable
        columnsList={getPRHeadersConfig(navigate, storeList)}
        dataList={prList.results || []}
        filters={filters}
        pageCount={prList.totalPages}
        onChange={(page) => {
          const newFilters = { ...filters, page };
          setFilters(newFilters);
          loadData(newFilters);
        }}
      />
    </div>
  );
};

export default PRList;
